/**
 * The Brain's shell. Outside a tenant the sidebar is the Brain's own
 * navigation; inside /tenants/<id> it becomes that tenant's navigation and
 * the inverse band names the tenant, its host, status and placement.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { Shell, Crumbs, type NavSection, type SearchResult } from "../kit/Shell";
import { Icon, Flag } from "../kit/Icon";
import { api, post, useApi } from "../kit/api";
import { Dot, healthTone } from "../kit/ui";
import { useCan, useMe } from "./context";
import { MyNotifications } from "../pages/notifications/MyNotifications";

type Counts = { tenants: number; partners: number; jobs_active: number; tickets_open: number; tickets_overdue: number; approvals: number; hosts_offline: number; docs_requests?: number };

const PAGES: { to: string; label: string; section: string; icon: string; area: string }[] = [
  { to: "/", label: "Overview", section: "", icon: "grid", area: "" },
  { to: "/tenants", label: "Tenants", section: "Customers", icon: "building-multiple", area: "tenants" },
  { to: "/partners", label: "Partners", section: "Customers", icon: "handshake", area: "partners" },
  { to: "/provisioning", label: "Provisioning", section: "Customers", icon: "arrow-sync-circle", area: "provisioning" },
  { to: "/tickets", label: "Tickets", section: "Support", icon: "ticket-diagonal", area: "tickets" },
  { to: "/feedback", label: "Feedback", section: "Support", icon: "person-feedback", area: "feedback" },
  { to: "/releases", label: "Platform updates", section: "Releases", icon: "rocket", area: "platform_updates" },
  { to: "/windows", label: "Maintenance windows", section: "Releases", icon: "calendar-clock", area: "windows" },
  { to: "/agents", label: "Agent updates", section: "Releases", icon: "arrow-download", area: "agent_updates" },
  { to: "/announcements", label: "Announcements", section: "Outreach", icon: "megaphone", area: "announcements" },
  { to: "/email", label: "Email & notifications", section: "Outreach", icon: "mail", area: "email" },
  { to: "/docs", label: "Documentation", section: "Outreach", icon: "book-open", area: "docs" },
  { to: "/status", label: "Service status", section: "Platform", icon: "pulse", area: "service_status" },
  { to: "/hosts", label: "Hosts & operations", section: "Platform", icon: "server", area: "fleet" },
  { to: "/clouds", label: "Clouds & regions", section: "Platform", icon: "cloud", area: "clouds" },
  { to: "/quotes", label: "Quotes & Costing", section: "Business", icon: "receipt-money", area: "quotes" },
  { to: "/team", label: "Team", section: "Administration", icon: "people-team", area: "team" },
  { to: "/roles", label: "Roles & permissions", section: "Administration", icon: "shield-person", area: "roles" },
  { to: "/approvals", label: "Approvals", section: "Administration", icon: "checkmark-starburst", area: "" },
  { to: "/audit", label: "Audit trail", section: "Administration", icon: "history", area: "audit" },
  { to: "/settings", label: "Settings", section: "Administration", icon: "settings", area: "" },
];

export const TENANT_PAGES = [
  { to: "", label: "Overview", icon: "grid", section: "" },
  { to: "licence", label: "Licence & edition", icon: "receipt", section: "Manage" },
  { to: "infrastructure", label: "Infrastructure", icon: "server", section: "Manage" },
  { to: "region", label: "Region & PoPs", icon: "globe-location", section: "Manage" },
  { to: "access", label: "Names & API access", icon: "key", section: "Manage" },
  { to: "updates", label: "Updates", icon: "arrow-circle-up", section: "Manage" },
  { to: "status", label: "Service status", icon: "pulse", section: "Operate" },
  { to: "support", label: "Support", icon: "ticket-diagonal", section: "Operate" },
  { to: "owner", label: "Owner & admins", icon: "person-key", section: "Operate" },
  { to: "policy", label: "Tenant policy", icon: "options", section: "Operate" },
  { to: "activity", label: "Activity", icon: "history", section: "Operate" },
  { to: "control-center", label: "Control Center", icon: "window-shield", section: "Access" },
  { to: "docs", label: "Docs access", icon: "book-open", section: "Access" },
  { to: "danger", label: "Danger zone", icon: "warning", section: "Access" },
];

export function Layout({ children }: { children: ReactNode }) {
  const me = useMe();
  const can = useCan();
  const loc = useLocation();
  const nav = useNavigate();
  const [myMail, setMyMail] = useState(false);
  const counts = useApi<Counts>("/api/counts", { poll: 60_000 });
  const m = /^\/tenants\/(?!new)([^/]+)(?:\/([^/]+))?/.exec(loc.pathname);
  const tenantId = m?.[1] ?? null;
  const tenant = useApi<{ tenant: { id: string; name: string; slug: string; admin_url: string; status: string; health: string; plan: string; partner_id: string | null; deployment: string }; region: { city: string; country: string } | null; provider: { name: string } | null; partner: { name: string } | null; tickets: unknown[] }>(tenantId ? `/api/tenants/${tenantId}` : null);
  const c = counts.data;
  // Approvals is for Super admins and Owners, who decide, and for anyone whose roles can ask for one.
  const approvals = me.roles.some((r) => (r.id === "builtin:super_admin" || r.id === "builtin:owner") && r.applies) || can("tenants", "delete") || can("tenants", "suspend") || can("break_glass", "read_write");
  const offered = (pg: (typeof PAGES)[number]) => (pg.to === "/approvals" ? approvals : !pg.area || can(pg.area));

  const sections: NavSection[] = useMemo(() => {
    if (tenantId) {
      const groups: NavSection[] = [];
      for (const pg of TENANT_PAGES) {
        let g = groups.find((x) => (x.title ?? "") === pg.section);
        if (!g) groups.push((g = { title: pg.section || undefined, items: [] }));
        g.items.push({ to: `/tenants/${tenantId}${pg.to ? `/${pg.to}` : ""}`, label: pg.label, icon: pg.icon, end: !pg.to, count: pg.to === "support" ? tenant.data?.tickets.length : undefined, badge: true, locked: pg.to === "control-center" && !can("break_glass") ? "Your roles don't include Break-glass access." : pg.to === "docs" && !can("docs") ? "Your roles don't include Documentation." : false });
      }
      return groups;
    }
    const groups: NavSection[] = [];
    for (const pg of PAGES) {
      if (!offered(pg)) continue;
      let g = groups.find((x) => (x.title ?? "") === pg.section);
      if (!g) groups.push((g = { title: pg.section || undefined, items: [] }));
      const count = pg.to === "/tenants" ? c?.tenants : pg.to === "/partners" ? c?.partners : pg.to === "/provisioning" ? c?.jobs_active : pg.to === "/tickets" ? c?.tickets_open : pg.to === "/approvals" ? c?.approvals : pg.to === "/hosts" ? c?.hosts_offline || undefined : pg.to === "/docs" ? c?.docs_requests || undefined : undefined;
      g.items.push({ to: pg.to, label: pg.label, icon: pg.icon, end: pg.to === "/", count, badge: pg.to === "/tickets" || pg.to === "/approvals" || pg.to === "/docs" });
    }
    return groups;
  }, [tenantId, tenant.data, c, can, approvals]); // eslint-disable-line react-hooks/exhaustive-deps

  const crumbs = useMemo(() => {
    if (tenantId) {
      const sub = TENANT_PAGES.find((p) => p.to === (m?.[2] ?? ""));
      return <Crumbs items={[{ label: "Tenants", to: "/tenants" }, { label: tenant.data?.tenant.name ?? "…", to: `/tenants/${tenantId}` }, sub?.label ?? "Overview"]} />;
    }
    const pg = [...PAGES].sort((a, b) => b.to.length - a.to.length).find((p) => (p.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(p.to)));
    const extra = loc.pathname === "/tenants/new" ? ["New tenant"] : /^\/(partners|provisioning|hosts)\/./.test(loc.pathname) ? ["Detail"] : [];
    return <Crumbs items={["Brain", ...(pg?.section ? [pg.section] : []), ...(pg && pg.to !== "/" ? [extra.length ? { label: pg.label, to: pg.to } : pg.label] : ["Overview"]), ...extra]} />;
  }, [loc.pathname, tenantId, tenant.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = tenant.data;
  const band = tenantId ? (
    <>
      <button onClick={() => nav("/tenants")}>
        <Icon name="arrow-left" size={14} /> All tenants
      </button>
      <span className="bsep" />
      <b>{t?.tenant.name ?? "…"}</b>
      <span className="mono ellipsis" style={{ maxWidth: 320 }}>{t?.tenant.admin_url.replace("https://", "")}</span>
      {t && (
        <span className="row" style={{ gap: 6, border: "1px solid var(--invSep)", borderRadius: 6, padding: "2px 8px" }}>
          <Dot tone={healthTone(t.tenant.status)} />
          {t.tenant.status[0].toUpperCase() + t.tenant.status.slice(1)}
        </span>
      )}
      {t?.region && (
        <span className="row" style={{ gap: 6 }}>
          <Flag code={t.region.country} /> {t.region.city}
          {t.provider ? ` · ${t.provider.name}` : t.tenant.deployment === "on_premises" ? " · On-premises" : ""}
        </span>
      )}
      <span className="right row" style={{ gap: 10 }}>
        <span className="row" style={{ gap: 6, color: "var(--invTx2)" }}>
          <Icon name="eye" size={14} /> Audited
        </span>
      </span>
    </>
  ) : undefined;

  const quick: SearchResult[] = useMemo(() => PAGES.filter(offered).map((p) => ({ kind: "Page", title: p.label, sub: p.section || "Brain", href: p.to, icon: p.icon })), [can, approvals]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Shell
      product={{ name: "Inlinea", sub: "Brain", tag: "Internal" }}
      nav={sections}
      header={
        tenantId && t ? (
          <button className="ctx" onClick={() => nav(`/tenants/${tenantId}`)}>
            <span className="avatar" style={{ width: 30, height: 30, background: "var(--blF)" }}>{t.tenant.name.slice(0, 2).toUpperCase()}</span>
            <span className="grow" style={{ textAlign: "left", minWidth: 0 }}>
              <div className="ellipsis" style={{ fontWeight: 600 }}>{t.tenant.name}</div>
              <div className="faint ellipsis" style={{ fontSize: 11.5 }}>{t.tenant.plan[0].toUpperCase() + t.tenant.plan.slice(1)}{t.partner ? ` · ${t.partner.name}` : " · Direct"}</div>
            </span>
          </button>
        ) : undefined
      }
      crumbs={crumbs}
      band={band}
      topRight={
        <span className="envchip" title={me.environment === "production" ? "You are working on production." : `Environment: ${me.environment}`}>
          <span className={`live${me.environment === "production" ? "" : " am"}`} />
          {me.environment === "production" ? "Production" : me.environment[0].toUpperCase() + me.environment.slice(1)}
          <span className="mono">{me.host}</span>
        </span>
      }
      user={{
        name: me.member.name,
        email: me.member.email,
        lines: (
          <div className="faint row" style={{ fontSize: 12, gap: 5, marginTop: 2 }}>
            <Icon name={me.factor === "key" ? "key" : "shield-lock"} size={12} />
            {me.factor === "key" ? "Signed in with a security key" : me.factor === "dev" ? "Local development session" : me.factor === "totp" ? "Signed in with an authenticator code" : "Signed in without a security key"}
          </div>
        ),
        menu: [
          ...(can("roles") ? [{ label: "My roles", icon: "shield-person", onClick: () => nav("/roles") }] : []),
          { label: "My notifications", icon: "mail", onClick: () => setMyMail(true) },
          { label: "Settings", icon: "settings", onClick: () => nav("/settings") },
          {
            label: "Sign out",
            icon: "sign-out",
            onClick: () => post("/auth/logout").finally(() => window.location.assign("/signin")),
          },
        ],
      }}
      version={
        me.version.version ? (
          <>
            <b className="mono">v{me.version.version}</b>
            <span className="faint mono">Build {me.version.build}</span>
          </>
        ) : (
          <span className="faint">No release recorded yet</span>
        )
      }
      search={(q) =>
        api<{ results: { kind: string; title: string; sub: string; href: string }[] }>(`/api/search?q=${encodeURIComponent(q)}`).then((r) =>
          r.results.map((x) => ({ ...x, icon: x.kind === "Tenant" ? "building" : x.kind === "Partner" ? "handshake" : x.kind === "Ticket" ? "ticket-diagonal" : x.kind === "Job" ? "arrow-sync-circle" : "person" })),
        )
      }
      quick={quick}
    >
      {children}
      <MyNotifications open={myMail} onClose={() => setMyMail(false)} />
    </Shell>
  );
}
